import { getAPIManagerInstance } from '../../../api/apiManager';
import { awsConfig } from '../../../aws-config/aws-config';
import {
  clearActiveSession,
  clearEmail,
  clearPersistedState,
  clearTokens,
  getActiveSession,
  getEmail,
  saveActiveSession,
  saveEmail,
  saveTokens,
} from '../../../common/tokenPersistence';
import { persistor } from '../../store';
import {
  isNullUndefinedOrEmptyObject,
  isNullUndefinedOrEmptyString,
} from '../../../common/utils';
import {
  logout,
  setCognitoEmailAddress,
  setCognitoUsername,
  setUserDetails,
  setAuthCodeRequired,
  setAccessToken,
  setIdToken,
  setProgressMessage,
  setErrorMessage,
  workStarted,
  workFailed,
  workSucceeded,
  setCreateAccountEmailAddress,
  setIsAccountCreationCompleted,
  setIsUserLoginCompleted,
  setSendAuthCodeInProgress,
  setSubmitAuthCodeInProgress,
} from './authSlice';
import {
  signIn,
  signOut,
  confirmSignIn,
  fetchAuthSession,
} from 'aws-amplify/auth';
import { AppThunk } from '../../store';
import {
  AccountDetails,
  UserDetails,
  ValidateCognitoEmailResult,
} from '../../../types/auth';
import apiAccess from '../../../api/apiAccess';

type ApiAccess = typeof apiAccess;

// Thunks - these are asynchronous functions that interact with reducers in predictable ways
// All api interactions should be performed via a "thunk" so that api request state can
// be updated in the Redux store rather than at the component level

export const validateCognitoEmailAddress =
  (
    emailAddress: string
  ): AppThunk<Promise<ValidateCognitoEmailResult>, ApiAccess> =>
  async (dispatch, _getState, api) => {
    dispatch(workStarted());
    try {
      const result =
        await api.getCognitoUserIdByEmailAddressAsync(emailAddress);

      if (Object.keys(result).length !== 0) {
        dispatch(workFailed());
        return {
          success: false,
          userExists: true,
          validationError: false,
        };
      } else {
        dispatch(setCreateAccountEmailAddress(emailAddress));
        dispatch(workSucceeded());
        return {
          success: true,
          userExists: false,
          validationError: false,
        };
      }
    } catch {
      dispatch(workFailed());
      dispatch(
        setErrorMessage(
          'An unexpected error occurred while attempting validate the provided email address.'
        )
      );
      return {
        success: false,
        userExists: false,
        validationError: true,
      };
    }
  };

export const retrieveCognitoUserAsync =
  (emailAddress: string): AppThunk<Promise<void>, ApiAccess> =>
  async (dispatch, _getState, api) => {
    dispatch(
      setProgressMessage({
        message: 'Initializing login procress...',
      })
    );
    dispatch(logout());
    clearPersistedState();
    dispatch(
      setProgressMessage({
        message: 'Retrieving associated user identity...',
      })
    );
    dispatch(workStarted());
    try {
      // Check email address validity
      if (isNullUndefinedOrEmptyString(emailAddress)) {
        dispatch(
          setErrorMessage({
            message: 'Email address not supplied',
          })
        );
        dispatch(workFailed());
        return;
      }
      // Make API call
      const userDetails =
        await api.getCognitoUserIdByEmailAddressAsync(emailAddress);

      // Handle empty response
      if (isNullUndefinedOrEmptyObject(userDetails)) {
        dispatch(
          setErrorMessage({
            message: 'Email address does not match known user.',
          })
        );
        dispatch(workFailed());
        return;
      }

      // Only persist identity after confirming the user exists in this portal
      dispatch(setCognitoEmailAddress(emailAddress));
      saveEmail(emailAddress);

      // On succcess, update state
      dispatch(
        setCognitoUsername({
          cognitoUsername: `${getAPIManagerInstance().getPlaceUrl()}::userid/${userDetails.userId}`,
        })
      );
      dispatch(performSignInAsync());
    } catch (error) {
      dispatch(setErrorMessage({ message: (error as Error).message }));
      dispatch(workFailed());
    }
  };

export const performSignInAsync =
  (isAccountCreation = false): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    dispatch(setSendAuthCodeInProgress(true));

    const progressMessage =
      isAccountCreation === true
        ? 'Creating new account...'
        : 'Signing in user...';
    let errorMessage = '';
    dispatch(setProgressMessage({ message: progressMessage }));
    try {
      await signOut();
      const { auth } = getState();
      const { nextStep } = await signIn({
        username: auth.cognitoUsername,
        options: {
          authFlowType: 'CUSTOM_WITHOUT_SRP',
          clientId:
            awsConfig[
              process.env.REACT_APP_ENVIRONMENT as keyof typeof awsConfig
            ].Auth.Cognito.userPoolClientId,
          authParameters: {
            USERNAME: auth.cognitoUsername,
          },
          ClientMetadata: {},
        },
      });
      if (!nextStep) {
        errorMessage =
          isAccountCreation === true
            ? 'Unable to create new account!'
            : 'Unable to identify user by email address!';
        dispatch(
          setErrorMessage({
            message: errorMessage,
          })
        );
        dispatch(workFailed());
      } else {
        const nextProgressMessage = isAccountCreation
          ? 'Validating user email...'
          : 'Validating user signin request...';
        dispatch(
          setProgressMessage({
            message: nextProgressMessage,
          })
        );
        const challengeEmail = auth.cognitoEmailAddress || getEmail() || '';

        // If there is no email address to use for login confirmation
        // login fails
        if (isNullUndefinedOrEmptyString(challengeEmail)) {
          dispatch(
            setErrorMessage({
              message:
                'Unexpected error occured while communicating with authorization server.',
            })
          );
          dispatch(workFailed());
        } else {
          const { nextStep: confirmNextStep } = await confirmSignIn({
            challengeResponse: challengeEmail.toLowerCase(),
          });

          // Handle unexpected result
          if (!confirmNextStep) {
            dispatch(
              setErrorMessage({
                message:
                  'The email address was changed during authorization process.',
              })
            );
            dispatch(workFailed());
          } else {
            dispatch(setAuthCodeRequired(true));
          }
        }
      }
    } catch {
      dispatch(
        setErrorMessage({
          message: 'Unknown error occurred while retrieving associated user.',
        })
      );
      dispatch(workFailed());
    } finally {
      dispatch(setSendAuthCodeInProgress(false));
    }
  };

export const submitAuthCode =
  (
    authCode: string,
    isAccountCreation = false
  ): AppThunk<Promise<void>, ApiAccess> =>
  async (dispatch, getState, api) => {
    dispatch(setSubmitAuthCodeInProgress(true));
    dispatch(
      setProgressMessage({
        message: 'Confirming authorization code...',
      })
    );
    try {
      const { nextStep } = await confirmSignIn({
        challengeResponse: authCode,
      });
      dispatch(setProgressMessage('Authorizing...'));
      // Handle failure here
      if (!nextStep) {
        dispatch(
          setErrorMessage({
            message: 'Unable to confirm authorization code.',
          })
        );
        dispatch(workFailed());
      } else {
        dispatch(
          setProgressMessage({
            message: 'Fetching user session...',
          })
        );
        // If successful, get the session
        const { tokens } = await fetchAuthSession();
        dispatch(setProgressMessage('Fetching user session...'));

        // Update the tokens in redux
        dispatch(setAccessToken(tokens?.accessToken?.toString()));
        dispatch(setIdToken(tokens?.idToken?.toString()));

        // Store in local storage
        saveTokens(
          tokens?.accessToken?.toString(),
          tokens?.idToken?.toString()
        );

        // Get user details using access token
        const authEmail =
          getState().auth.cognitoEmailAddress || getEmail() || '';
        try {
          const userDetailsResult = await api.getUserDetailsAuthTokenAsync(
            authEmail,
            tokens?.idToken
          );
          dispatch(setUserDetails(userDetailsResult));
        } catch (error) {
          dispatch({ type: 'resetRegSystem' });
          dispatch(setErrorMessage({ message: (error as Error).message }));
          throw error;
        }

        // Persist in local storage
        saveEmail(authEmail);

        // Mark this portal's user as the active session so other portal tabs
        // can detect the change on their next load.
        saveActiveSession(getState().auth.cognitoUsername);

        // Force an immediate synchronous write of the current Redux state to
        // localStorage. Without this, redux-persist's async write may not
        // complete before the full-page reload triggered by login completion,
        // leaving persist:root:${portalKey} empty on the next page load and
        // causing RequireAuth to redirect to login despite valid tokens.
        await persistor.flush();

        if (isAccountCreation === true) {
          dispatch(setIsAccountCreationCompleted(true));
        } else {
          dispatch(setIsUserLoginCompleted(true));
        }

        dispatch(workSucceeded());
        dispatch(setAuthCodeRequired(false));
      }
    } catch {
      dispatch(
        setErrorMessage({
          message: 'Unknown error occurred while confirming auth code.',
        })
      );
      dispatch(workFailed());
      dispatch(setAuthCodeRequired(true));
    } finally {
      dispatch(setSubmitAuthCodeInProgress(false));
    }
  };

export const refreshSession =
  (
    forceRefresh = false
  ): AppThunk<
    Promise<Awaited<ReturnType<typeof fetchAuthSession>>['tokens']>,
    ApiAccess
  > =>
  async (dispatch, getState, api) => {
    // Read both signals synchronously before any await so concurrent calls
    // to validateSession cannot clear these values mid-flight.
    const { cognitoUsername, cognitoEmailAddress: currentCognitoEmail } =
      getState().auth;

    const { tokens } = await fetchAuthSession({ forceRefresh });

    if (tokens) {
      // Update the tokens in redux. Guard against Amplify returning a `tokens`
      // object whose individual token fields are null/undefined at runtime —
      // dispatching setAccessToken(undefined) would cause JSON.stringify to
      // omit the key, so the next rehydration would fall back to '' and
      // RequireAuth would redirect to login despite a valid session.
      const newAccessToken = tokens.accessToken?.toString();
      const newIdToken = tokens.idToken?.toString();
      if (newAccessToken) {
        dispatch(setAccessToken(newAccessToken));
      }
      if (newIdToken) {
        dispatch(setIdToken(newIdToken));
      }

      if (!currentCognitoEmail || currentCognitoEmail === '') {
        const storedEmail = getEmail();
        if (storedEmail) {
          try {
            const userDetailsResult = await api.getUserDetailsAuthTokenAsync(
              storedEmail,
              tokens.idToken
            );
            dispatch(setUserDetails(userDetailsResult));
          } catch {
            // getUserDetailsAuthTokenAsync is a best-effort recovery to populate
            // userDetails when cognitoEmailAddress is missing from the persisted
            // state. A failure here must not destroy the session — the Amplify
            // tokens are still valid and the user should remain logged in.
          }
        }
      }
      saveTokens(newAccessToken, newIdToken);
    } else {
      // fetchAuthSession() returns undefined when signOut() was called globally —
      // this happens transiently while another portal is mid-login (they called
      // signOut before their own signIn/confirmSignIn completes).
      // Only wipe this portal's session if the active-session marker has already
      // moved to another portal. If it still points here, another portal is just
      // mid-flow; keep existing Redux tokens and let the first 401 redirect.
      const active = getActiveSession();
      if (!cognitoUsername || (active && active !== cognitoUsername)) {
        dispatch(setAccessToken(undefined));
        dispatch(setIdToken(undefined));
        clearTokens();
        clearEmail();
      }
    }
    return tokens;
  };

export const terminateSession =
  (): AppThunk<Promise<void>> => async (_dispatch, getState) => {
    const { cognitoUsername } = getState().auth;
    clearTokens();
    clearEmail();
    // Only remove the shared active-session marker if it still points to this
    // portal's user. If another portal logged in more recently, leave their
    // marker in place so their validateSession check continues to pass.
    const active = getActiveSession();
    if (active === cognitoUsername) {
      clearActiveSession();
    }
  };

export const createAccount =
  (newAccountDetails: AccountDetails): AppThunk<Promise<void>, ApiAccess> =>
  async (dispatch, _getState, api) => {
    const {
      companyName,
      email,
      firstName,
      lastName,
      mailingAddress,
      mailingAddressComponents,
      phone,
    } = newAccountDetails;

    dispatch(workStarted());
    dispatch(
      setProgressMessage({
        message: 'Creating account...',
      })
    );
    try {
      const preparedPayload = {
        companyName,
        email,
        firstName,
        lastName,
        mailingAddress,
        misc: {
          mailingAddressComponents,
        },
        phoneNumber: phone,
      };
      const userDetails = await api.createAccountAsync(preparedPayload);
      if (userDetails.userId) {
        dispatch(
          setProgressMessage({
            message: 'Account created! Authorizing...',
          })
        );
        dispatch(workSucceeded());
        dispatch(
          setCognitoUsername({
            cognitoUsername: `${getAPIManagerInstance().getPlaceUrl()}::userid/${userDetails.userId}`,
          })
        );
        dispatch(setCognitoEmailAddress(userDetails.email));
        dispatch(performSignInAsync(true));
      } else {
        dispatch(setErrorMessage({ message: 'Unable to create account.' }));
        dispatch(workFailed());
      }
    } catch (err) {
      dispatch(
        setErrorMessage({
          message:
            (err as Error)?.message ??
            'An unexpected error occurred while creating the account.',
        })
      );
      dispatch(workFailed());
    }
  };

export const validateSession = (): AppThunk => (dispatch, getState) => {
  const { cognitoUsername } = getState().auth;

  // No stored session for this portal — nothing to validate.
  if (!cognitoUsername) {
    return;
  }

  // Check if another portal has logged in since this session was established.
  // saveActiveSession writes this key on every successful login, so it always
  // reflects whoever authenticated most recently across all portals.
  const activeSession = getActiveSession();
  if (activeSession && activeSession !== cognitoUsername) {
    dispatch({ type: 'resetRegSystem' });
    clearPersistedState();
  }
};

export const updateAccountDetails =
  (
    userID: string,
    updatedDetails: AccountDetails
  ): AppThunk<Promise<void>, ApiAccess> =>
  async (dispatch, getState, api) => {
    const {
      companyName,
      email,
      firstName,
      lastName,
      mailingAddress,
      mailingAddressComponents,
      phone,
    } = updatedDetails;

    try {
      dispatch(workStarted());
      dispatch(
        setProgressMessage({
          message: 'updating account details...',
        })
      );

      const preparedPayload = {
        accessToken: getState().auth?.idToken,
        companyName,
        email,
        firstName,
        lastName,
        mailingAddress,
        misc: {
          mailingAddressComponents,
        },
        phoneNumber: phone,
        userId: userID,
      };

      // Get user details using access token
      const result = await api.editPersonDetailsAsync(preparedPayload);

      if (result && result?.status === 200) {
        dispatch(workSucceeded());
        dispatch(setUserDetails(updatedDetails as UserDetails));
      } else {
        dispatch(
          setErrorMessage({ message: 'Unable to update account details.' })
        );
        dispatch(workFailed());
      }
    } catch {
      dispatch(
        setErrorMessage({
          message:
            'Unknown error occurred while trying to update account details.',
        })
      );
      dispatch(workFailed());
    }
  };
